import { AxiosResponse } from 'axios'
import Request from 'redux/config/services/Request'
import { AutoEnrollRule } from './autoEnrollTypes'
import { hasConditionValue } from 'features/RuleBuilder'

const BASE_PATH = '/hr/profile/auto_enrollment_rules/'

const clearedSecondary = {
  secondaryField: null,
  secondaryOperator: null,
  secondaryValue: null,
  conditionJoin: null,
}

const clearedTertiary = {
  tertiaryField: null,
  tertiaryOperator: null,
  tertiaryValue: null,
  tertiaryConditionJoin: null,
}

/** Build API body including explicit nulls when optional conditions are absent. */
const autoEnrollRuleToApiBody = (
  payload: Omit<AutoEnrollRule, 'id'> | AutoEnrollRule
) => {
  const secondaryOperator = payload.secondaryOperator ?? 'equals'
  const hasSecondary =
    Boolean(payload.secondaryField) &&
    hasConditionValue(payload.secondaryValue ?? '', secondaryOperator)
  const tertiaryOperator = payload.tertiaryOperator ?? 'equals'
  const hasTertiary =
    hasSecondary &&
    Boolean(payload.tertiaryField) &&
    hasConditionValue(payload.tertiaryValue ?? '', tertiaryOperator)

  const base = {
    field: payload.field,
    operator: payload.operator,
    value: payload.value,
    segments: payload.segments ?? [],
    tags: payload.tags ?? [],
    isActive: payload.isActive ?? true,
    removeNonMatchingMembers: payload.removeNonMatchingMembers ?? false,
  }

  if (!hasSecondary) {
    return { ...base, ...clearedSecondary, ...clearedTertiary }
  }

  const withSecondary = {
    ...base,
    secondaryField: payload.secondaryField,
    secondaryOperator,
    secondaryValue: payload.secondaryValue,
    conditionJoin: payload.conditionJoin ?? 'and',
  }

  if (!hasTertiary) {
    return { ...withSecondary, ...clearedTertiary }
  }

  return {
    ...withSecondary,
    tertiaryField: payload.tertiaryField,
    tertiaryOperator,
    tertiaryValue: payload.tertiaryValue,
    tertiaryConditionJoin: payload.tertiaryConditionJoin ?? 'and',
  }
}

const getAutoEnrollmentRules = (): Promise<AxiosResponse<AutoEnrollRule[]>> => {
  return Request.get(BASE_PATH)
}

const createAutoEnrollmentRule = (
  payload: Omit<AutoEnrollRule, 'id'>
): Promise<AxiosResponse<AutoEnrollRule>> => {
  return Request.post(BASE_PATH, autoEnrollRuleToApiBody(payload))
}

const getAutoEnrollmentRule = (
  id: string
): Promise<AxiosResponse<AutoEnrollRule>> => {
  return Request.get(`${BASE_PATH}${id}/`)
}

const modifyAutoEnrollmentRule = (
  id: string,
  payload: AutoEnrollRule
): Promise<AxiosResponse<AutoEnrollRule>> => {
  return Request.put(`${BASE_PATH}${id}/modify/`, autoEnrollRuleToApiBody(payload))
}

const deleteAutoEnrollmentRule = (id: string): Promise<AxiosResponse<void>> => {
  return Request.del(`${BASE_PATH}${id}/`)
}

export default {
  getAutoEnrollmentRules,
  createAutoEnrollmentRule,
  getAutoEnrollmentRule,
  modifyAutoEnrollmentRule,
  deleteAutoEnrollmentRule,
}
